import {
  Component,
  computed,
  input,
  output,
  signal,
  effect,
  ChangeDetectionStrategy,
  ContentChild,
  TemplateRef,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SoSvgIconUI } from '../so-svg-icon/so-svg-icon.component';
import { SoPremiumIconUI } from '../so-premium-icon/so-premium-icon.component';

export interface SoMenuItem {
  label: string;
  route?: string;
  icon?: string;
  disabled?: boolean;
  badge?: string | number;
  children?: SoMenuItem[];
  color?: string;
  showUploadingIndicator?: boolean;
}

export interface SoUserMenuAction {
  id: string;
  label: string;
  icon?: string;
  color?: string;
  action?: Function;
}

const SIDEBAR_STORAGE_KEY = 'so-sidebar-collapsed-state';

@Component({
  selector: 'so-section-layout-ui',
  standalone: true,
  imports: [CommonModule, RouterModule, SoSvgIconUI, SoPremiumIconUI],
  templateUrl: './so-section-layout.component.html',
  styleUrl: './so-section-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoSectionLayoutUI implements OnDestroy {
  @ContentChild('headerContent') headerContent?: TemplateRef<unknown>;
  @ContentChild('sidebarHeader') sidebarHeader?: TemplateRef<unknown>;
  @ContentChild('sidebarFooter') sidebarFooter?: TemplateRef<unknown>;

  // Modern signal-based inputs
  menuItems = input<SoMenuItem[]>([]);
  title = input<string>('');
  subtitle = input<string>('');
  logoText = input<string>('');
  logoIcon = input<string>('');
  version = input<string>('');
  
  // Layout configuration
  sidebarWidth = input<string>('260px');
  sidebarCollapsedWidth = input<string>('72px');
  minSidebarWidth = input<string>('200px');
  maxSidebarWidth = input<string>('420px');
  resizable = input<boolean>(false);
  collapsible = input<boolean>(true);
  initialCollapsed = input<boolean>(false);
  showHeader = input<boolean>(true);
  headerHeight = input<string>('64px');
  
  // Theme
  showThemeToggle = input<boolean>(true);
  
  // Active state
  activeRoute = input<string>('');
  queryParams = input<Record<string, unknown> | undefined>(undefined);
  uploadingStates = input<Record<string, boolean>>({});
  uploadingLabel = input<string>('Subiendo');

  // User menu configuration (header dropdown)
  userImage = input<string>('');
  userName = input<string>('');
  userRole = input<string>('');
  showUserMenu = input<boolean>(false);
  showLogoutButton = input<boolean>(false);
  showFooter = input<boolean>(false);
  logoutLabel = input<string>('Cerrar sesión');
  userMenuActions = input<SoUserMenuAction[]>([]);

  // Outputs
  sidebarToggled = output<boolean>();
  themeToggled = output<boolean>();
  menuItemClicked = output<SoMenuItem>();
  logoutClicked = output<void>();
  userMenuActionClicked = output<SoUserMenuAction>();

  // Internal state
  private hasInitialized = false;
  protected isCollapsed = signal<boolean>(false);
  protected isUserMenuOpen = signal<boolean>(false);
  protected currentWidth = signal<number | null>(null);
  protected isResizing = signal<boolean>(false);
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private readonly handleMouseMove = (event: MouseEvent) => this.onResizing(event);
  private readonly handleMouseUp = () => this.stopResizing();

  constructor() {
    // Initialize from inputs and localStorage
    const storedState = this.getStoredSidebarState();
    const initialState = storedState !== null ? storedState : this.initialCollapsed();
    this.isCollapsed.set(initialState);
    
    setTimeout(() => {
      this.hasInitialized = true;
      const initialWidth = this.parseWidth(this.sidebarWidth());
      if (initialWidth !== null) {
        this.currentWidth.set(initialWidth);
      }
    });

    // Persist sidebar state changes to localStorage (only after initialization)
    effect(() => {
      const collapsed = this.isCollapsed();
      if (this.hasInitialized) {
        this.storeSidebarState(collapsed);
      }
    });
  }

  ngOnDestroy(): void {
    this.stopResizing();
  }

  // Computed values
  protected containerClasses = computed(() => ({
    'so-layout': true,
    'so-layout--collapsed': this.isCollapsed()
  }));

  protected sidebarCurrentWidth = computed(() => {
    if (this.isCollapsed()) {
      return this.parseWidth(this.sidebarCollapsedWidth());
    }
    const base = this.parseWidth(this.sidebarWidth());
    const seed = this.currentWidth() ?? base;
    if (seed === null) return null;
    return this.clampWidth(seed);
  });

  protected sidebarStyles = computed(() => ({
    'width': this.toCssWidth(this.sidebarCurrentWidth(), this.isCollapsed() ? this.sidebarCollapsedWidth() : this.sidebarWidth()),
    'min-width': this.toCssWidth(this.sidebarCurrentWidth(), this.isCollapsed() ? this.sidebarCollapsedWidth() : this.sidebarWidth())
  }));

  protected mainStyles = computed(() => ({
    'margin-left': this.toCssWidth(this.sidebarCurrentWidth(), this.isCollapsed() ? this.sidebarCollapsedWidth() : this.sidebarWidth())
  }));

  protected headerStyles = computed(() => ({
    'height': this.headerHeight(),
    'left': this.toCssWidth(this.sidebarCurrentWidth(), this.isCollapsed() ? this.sidebarCollapsedWidth() : this.sidebarWidth())
  }));

  protected contentStyles = computed(() => ({
    'padding-top': this.showHeader() ? this.headerHeight() : '0'
  }));

  // Methods
  toggleSidebar(): void {
    if (!this.collapsible()) return;
    const newState = !this.isCollapsed();
    this.isCollapsed.set(newState);
    this.sidebarToggled.emit(newState);
  }

  private getStoredSidebarState(): boolean | null {
    const stored = this.safeGetLocalStorage(SIDEBAR_STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return null;
  }

  private storeSidebarState(collapsed: boolean): void {
    this.safeSetLocalStorage(SIDEBAR_STORAGE_KEY, collapsed.toString());
  }

  private safeGetLocalStorage(key: string): string | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private safeSetLocalStorage(key: string, value: string): void {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(key, value);
    } catch {
    }
  }

  toggleTheme(): void {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', newTheme);
    this.themeToggled.emit(newTheme === 'dark');
  }

  onMenuItemClick(item: SoMenuItem): void {
    if (item.disabled) return;
    this.menuItemClicked.emit(item);
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen.update(v => !v);
  }

  closeUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  onLogout(): void {
    this.closeUserMenu();
    this.logoutClicked.emit();
  }

  onUserMenuAction(action: SoUserMenuAction): void {
    this.closeUserMenu();
    if (action.action) {
      action.action();
    } else {
      this.userMenuActionClicked.emit(action);
    }
  }

  onResizeStart(event: MouseEvent): void {
    if (!this.resizable() || this.isCollapsed()) return;
    event.preventDefault();
    const startWidth = this.sidebarCurrentWidth();
    if (startWidth === null) return;
    this.isResizing.set(true);
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = startWidth;
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  private onResizing(event: MouseEvent): void {
    const delta = event.clientX - this.resizeStartX;
    const next = this.resizeStartWidth + delta;
    this.currentWidth.set(this.clampWidth(next));
  }

  private stopResizing(): void {
    this.isResizing.set(false);
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  private normalizeRoute(route: string | undefined): string {
    if (!route) return '';
    const cleaned = route.split('?')[0].split('#')[0];
    if (cleaned === '/') return '/';
    return cleaned.replace(/\/+$/g, '');
  }

  isActiveRoute(route: string | undefined): boolean {
    if (!route) return false;
    const current = this.normalizeRoute(this.activeRoute());
    const target = this.normalizeRoute(route);
    if (!target) return false;
    if (target === '/') return current === '/';
    return current === target || current.startsWith(`${target}/`);
  }
  
  isUploadingItem(item: SoMenuItem): boolean {
    if (!item.showUploadingIndicator) return false;
    const map = this.uploadingStates() || {};
    const normalizedRoute = this.normalizeRoute(item.route);
    if (normalizedRoute && normalizedRoute in map) {
      return !!map[normalizedRoute];
    }
    if (item.route && item.route in map) {
      return !!map[item.route];
    }
    if (item.label && item.label in map) {
      return !!map[item.label];
    }
    return false;
  }

  trackByMenuItem(index: number, item: SoMenuItem): string {
    return item.route || item.label;
  }

  private parseWidth(value: string | number | undefined): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const raw = (value ?? '').toString().trim();
    if (!raw) return null;
    const numericMatch = raw.match(/^(-?\d*\.?\d+)\s*(px)?$/i);
    if (!numericMatch) return null;
    const parsed = Number.parseFloat(numericMatch[1]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private clampWidth(value: number): number {
    const min = this.parseWidth(this.minSidebarWidth());
    const max = this.parseWidth(this.maxSidebarWidth());
    const clamped = Math.min(Math.max(value, min ?? -Infinity), max ?? Infinity);
    return Number.isFinite(clamped) ? clamped : value;
  }

  private toPx(value: number): string {
    return `${value}px`;
  }

  private toCssWidth(value: number | null, fallback: string | number | undefined): string {
    if (value !== null) return this.toPx(value);
    if (typeof fallback === 'number') return `${fallback}px`;
    const raw = (fallback ?? '').toString().trim();
    if (!raw) return '0';
    if (/^-?\d*\.?\d+$/.test(raw)) return `${raw}px`;
    return raw;
  }
}
